import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import Spinner from 'components/shared/DesignSystem/Spinner';
import ModalContent from './CreateReportFromTemplateContent';
import useGetTemplates from './gql/useGetTemplates';
import {
  ReportTemplateModal,
  CenteredLayout,
  Error,
  ErrorText,
  ErrorIcon,
  TemplateIcon,
} from './shared/ReportTemplateModal/styles';

import { Button } from '@companycam/slab-web';

const CreateReportFromTemplate = ({
  onRequestClose,
  projectNodeId,
  assets,
}) => {
  const {
    templates: reportTemplates,
    loading,
    error,
    hasNextPage,
    fetchMoreTemplates,
  } = useGetTemplates();

  const handleScroll = (e) => {
    const bottom =
      e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;

    if (bottom && hasNextPage && fetchMoreTemplates) {
      fetchMoreTemplates();
    }
  };

  const renderError = () => {
    return (
      <CenteredLayout>
        <Error>
          <ErrorIcon className="mdi mdi-alert-circle" aria-hidden="true" />
          <h2>
            <FormattedMessage
              id="Reports.templateEditModal.errorHeadline"
              defaultMessage="Well, Shoot."
              description="Error headine when template fails to load"
            />
          </h2>
          <ErrorText>
            <FormattedMessage
              id="Reports.templateListModal.errorMessage"
              defaultMessage="We hit an error loading your templates."
              description="Message when list of templates fails to load"
            />
          </ErrorText>
          <Button onClick={onRequestClose}>
            <FormattedMessage
              id="Common.close"
              defaultMessage="Close"
              description="Label for Close button in error state"
            />
          </Button>
        </Error>
      </CenteredLayout>
    );
  };

  const renderLoading = () => {
    return (
      <CenteredLayout>
        <Spinner
          size="large"
          layout="vertical"
          label={
            <FormattedMessage
              id="Reports.templateListModal.spinnerMessage"
              defaultMessage="Loading Templates..."
              description="Message when report templates modal is in loading state"
            />
          }
        />
      </CenteredLayout>
    );
  };

  const renderNoTemplates = () => (
    <CenteredLayout>
      <Error>
        <TemplateIcon className="mdi mdi-text-box-outline" aria-hidden="true" />
        <h2>
          <FormattedMessage
            id="Reports.templateListModal.noTemplatesHeadline"
            defaultMessage="No Templates Found"
            description="Error headine when no templates are found"
          />
        </h2>
        <ErrorText>
          <FormattedMessage
            id="Reports.templateListModal.noTemplatesMessage"
            defaultMessage="Please save a report as a template first."
            description="Error message when no templates are found"
          />
        </ErrorText>
        <Button onClick={onRequestClose}>
          <FormattedMessage
            id="Common.close"
            defaultMessage="Close"
            description="Label for Close button in error state"
          />
        </Button>
      </Error>
    </CenteredLayout>
  );

  const renderModalContent = () => {
    if (loading && reportTemplates.length === 0) return renderLoading();
    if (error) return renderError();
    if (!reportTemplates.length && !loading) return renderNoTemplates();

    return (
      <ModalContent
        reportTemplates={reportTemplates}
        handleScroll={handleScroll}
        projectNodeId={projectNodeId}
        assets={assets}
      />
    );
  };

  return (
    <ReportTemplateModal
      isOpen
      onRequestClose={onRequestClose}
      label="Use a Template to Create a Report"
      className="modal-content-wrap is-padless"
    >
      {renderModalContent()}
    </ReportTemplateModal>
  );
};

CreateReportFromTemplate.propTypes = {
  onRequestClose: PropTypes.func,
  projectNodeId: PropTypes.string,
  assets: PropTypes.object,
};

export default CreateReportFromTemplate;
